import type { ActivationRequest, MessageTemplate, SettingsView, User } from "@agency/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Link2, Pencil, Plus, RotateCcw, Trash2, Upload, X } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { callAction } from "../api";
import { useConfig } from "../components/ConfigProvider";
import { StateBadge } from "../components/StateBadge";
import { useToast } from "../components/Toast";
import { Badge, Button, Card, CardHeader, Checkbox, ConfirmDialog, Input, Select, Sheet, Textarea, cn } from "../components/ui";
import { useLocale, useT } from "../i18n";
import { formatPhone, shortDate } from "../theme";

const US_STATES = ["AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC"];
const ROLES = ["owner", "agent", "csr", "readonly", "techadmin"] as const;

export function SettingsScreen() {
  const t = useT();
  const { locale } = useLocale();
  const { config, devUser, switchUser } = useConfig();
  const qc = useQueryClient();
  const { toast } = useToast();
  const sv = useQuery({ queryKey: ["settings"], queryFn: async () => (await callAction<"agency.settings.get", SettingsView>("agency.settings.get", {})).result });
  const templates = useQuery({ queryKey: ["templates"], queryFn: async () => (await callAction<"agency.template.list", MessageTemplate[]>("agency.template.list", {})).result, staleTime: 300_000 });
  const [editing, setEditing] = useState<User | "new" | null>(null);
  const [confirmReset, setConfirmReset] = useState<null | "reload" | "remove">(null);

  const reset = useMutation({
    mutationFn: (reload: boolean) => callAction("agency.admin.reset_demo", { confirm: "reset", reload }),
    onSuccess: () => {
      qc.invalidateQueries();
      toast({ title: t("settings.demoData.done"), tone: "success" });
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });

  const intakeUrl = `${window.location.origin}/intake`;
  const copyIntake = async () => {
    try {
      await navigator.clipboard.writeText(intakeUrl);
      toast({ title: t("settings.copied"), tone: "success" });
    } catch {
      toast({ title: t("common.error"), body: intakeUrl, tone: "error" });
    }
  };

  const s = sv.data?.settings;
  const id = s?.identity;
  const notSet = t("settings.agency.notSet");

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-bold">{t("settings.title")}</h1>

      <AutomationsCard />

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-bold">{t("settings.connections")}</h2>
          <p className="text-sm text-ink/60">{t("settings.connections.sub")}</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {config.connectors.map((c) => (
            <Card key={c.id} className={cn("p-4", c.state === "not_connected" && "opacity-90")}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-bold">{c.label}</p>
                  <p className="text-xs text-ink/55 truncate">{t("settings.running")}: {c.provider}{c.configuredProvider ? ` · ${t("settings.configured")}: ${c.configuredProvider}` : ""}</p>
                </div>
                <StateBadge state={c.state} />
              </div>
              <p className="mt-2 text-sm text-ink/75">{c.note}</p>
              {c.lastError ? <p className="mt-1 text-xs text-red-600">{c.lastError}</p> : null}
            </Card>
          ))}
        </div>
      </section>

      <div className="grid gap-5 md:grid-cols-2">
        <Card>
          <CardHeader title={t("settings.agency")} subtitle={t("settings.agency.sub")} action={<Link to="/setup" className="inline-flex min-h-[36px] items-center gap-1 rounded-xl border border-ink/15 px-3 text-sm font-semibold hover:bg-ink/5"><Pencil className="h-3.5 w-3.5" />{t("settings.agency.edit")}</Link>} />
          <dl className="px-5 pb-5 grid grid-cols-[140px_1fr] gap-x-3 gap-y-1 text-sm">
            <dt className="text-ink/55">{t("setup.identity.legalName")}</dt><dd className="font-semibold">{id?.legalName ?? notSet} {id?.fictional ? <Badge tone="amber">{t("settings.agency.fictional")}</Badge> : null}</dd>
            <dt className="text-ink/55">{t("setup.identity.dba")}</dt><dd>{id?.dba ?? "—"}</dd>
            <dt className="text-ink/55">{t("setup.identity.address")}</dt><dd>{id?.address ? `${id.address.line1}, ${id.address.city}, ${id.address.state} ${id.address.zip}` : notSet}</dd>
            <dt className="text-ink/55">{t("setup.identity.phone")}</dt><dd>{formatPhone(id?.phone) ?? "—"}</dd>
            <dt className="text-ink/55">{t("setup.identity.email")}</dt><dd>{id?.email ?? "—"}</dd>
            <dt className="text-ink/55">{t("setup.identity.timezone")}</dt><dd>{id?.timezone ?? config.agency.timezone}</dd>
            <dt className="text-ink/55">{t("setup.identity.ein")}</dt><dd>{id?.ein ?? "—"}</dd>
            <dt className="text-ink/55">{t("setup.identity.npn")}</dt><dd>{id?.npn ?? "—"}</dd>
            <dt className="text-ink/55">{t("setup.identity.licenses")}</dt><dd>{id?.licenses?.length ? id.licenses.map((l) => `${l.state} ${l.number}`).join(", ") : "—"}</dd>
            <dt className="text-ink/55">{t("setup.profile.lines")}</dt><dd>{s?.profile.lines.length ? s.profile.lines.map((l) => l.replace(/_/g, " ")).join(", ") : notSet}</dd>
            <dt className="text-ink/55">{t("settings.carriers")}</dt><dd>{s?.profile.carriers.length ? s.profile.carriers.map((c) => c.name).join(", ") : config.agency.carriers.map((c) => c.name).join(", ") || notSet}</dd>
            <dt className="text-ink/55">{t("setup.profile.currentSystem")}</dt><dd>{s?.profile.currentSystem ?? "—"}</dd>
          </dl>
        </Card>

        <Card>
          <CardHeader title={t("settings.demoData")} subtitle={t("settings.demoData.sub")} />
          <div className="px-5 pb-5 space-y-3">
            <p className="text-sm">
              <Badge tone={sv.data?.sampleLoaded ? "green" : "neutral"}>{sv.data?.sampleLoaded ? t("settings.demoData.loaded") : t("settings.demoData.notLoaded")}</Badge>
              {sv.data ? <span className="ml-2 text-ink/60">{t("settings.demoData.counts").replace("{customers}", String(sv.data.counts.customers)).replace("{sample}", String(sv.data.counts.sampleCustomers)).replace("{policies}", String(sv.data.counts.policies))}</span> : null}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setConfirmReset("reload")} loading={reset.isPending}><RotateCcw className="h-4 w-4" />{t("settings.demoData.reload")}</Button>
              <Button variant="outline" onClick={() => setConfirmReset("remove")} loading={reset.isPending}><Trash2 className="h-4 w-4" />{t("settings.demoData.remove")}</Button>
            </div>
          </div>
          <CardHeader title={t("settings.importLink")} subtitle={t("settings.importLink.sub")} />
          <div className="px-5 pb-5">
            <Link to="/import" className="inline-flex min-h-tap items-center gap-2 rounded-xl bg-ink px-4 text-sm font-semibold text-white hover:bg-ink/90"><Upload className="h-4 w-4" />{t("settings.importLink.open")}</Link>
          </div>
        </Card>
      </div>

      <Card>
        <CardHeader title={t("settings.staff")} subtitle={t("settings.staff.sub")} action={<Button size="sm" onClick={() => setEditing("new")}><Plus className="h-4 w-4" />{t("settings.staff.add")}</Button>} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-ink/55">
              <tr>
                <th className="px-5 py-2">{t("settings.staff.name")}</th>
                <th className="px-3 py-2">{t("settings.staff.role")}</th>
                <th className="px-3 py-2 hidden md:table-cell">{t("settings.staff.email")}</th>
                <th className="px-3 py-2 hidden lg:table-cell">{t("settings.staff.npn")}</th>
                <th className="px-3 py-2 hidden lg:table-cell">{t("settings.staff.licenses")}</th>
                <th className="px-3 py-2">{t("common.status")}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {(sv.data?.users ?? []).map((u) => (
                <tr key={u.id} className="border-t border-ink/5">
                  <td className="px-5 py-2 font-semibold">{u.name}{u.title ? <span className="block text-xs font-normal text-ink/55">{u.title}</span> : null}</td>
                  <td className="px-3 py-2"><Badge>{t(`role.${u.role}`)}</Badge></td>
                  <td className="px-3 py-2 hidden md:table-cell">{u.email}</td>
                  <td className="px-3 py-2 hidden lg:table-cell">{u.npn ?? "—"}</td>
                  <td className="px-3 py-2 hidden lg:table-cell">{(u.licenses ?? []).length ? (u.licenses ?? []).map((l) => `${l.state} ${l.number}`).join(", ") : "—"}</td>
                  <td className="px-3 py-2">
                    <Badge tone={u.active ? "green" : "neutral"}>{u.active ? t("settings.staff.active") : t("settings.staff.inactive")}</Badge>
                    {u.invitation?.status === "pending" && !u.authSubject ? <Badge tone="amber" className="ml-1">{locale === "es" ? "Invitación enviada" : "Invited"}</Badge> : null}
                  </td>
                  <td className="px-3 py-2 text-right"><Button variant="ghost" size="sm" onClick={() => setEditing(u)}><Pencil className="h-4 w-4" />{t("settings.staff.edit")}</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <ActivationCard view={sv.data} />

      <div className="grid gap-5 md:grid-cols-2">
        {config.authMode === "dev" ? (
          <Card>
            <CardHeader title={t("settings.users")} subtitle={t("settings.users.sub")} />
            <div className="px-5 pb-5 space-y-2">
              {config.users.map((u) => (
                <button key={u.id} onClick={() => switchUser(u.id)} className={cn("flex w-full min-h-tap items-center justify-between rounded-xl border px-3 text-left", devUser === u.id ? "border-primary bg-primary/8" : "border-ink/10 hover:bg-ink/5")} aria-pressed={devUser === u.id}>
                  <span className="font-semibold">{u.name}</span>
                  <Badge>{t(`role.${u.role}`)}</Badge>
                </button>
              ))}
            </div>
          </Card>
        ) : null}
        <Card>
          <CardHeader title={t("settings.intake")} subtitle={t("settings.intake.sub")} />
          <div className="px-5 pb-5 flex flex-col gap-2 sm:flex-row sm:items-center">
            <a href="/intake" target="_blank" rel="noopener" className="flex-1 inline-flex min-h-tap items-center gap-2 rounded-xl bg-surface px-3 font-mono text-sm break-all hover:underline"><Link2 className="h-4 w-4 shrink-0" />{intakeUrl}</a>
            <Button variant="outline" onClick={copyIntake}><Copy className="h-4 w-4" />{t("settings.copyLink")}</Button>
          </div>
        </Card>
        <Card>
          <CardHeader title={t("settings.templates")} subtitle={t("settings.templates.sub")} />
          <ul className="px-5 pb-5 divide-y divide-ink/5">
            {templates.isLoading ? <li className="py-2 text-sm text-ink/55">{t("common.loading")}</li> : null}
            {templates.data?.length === 0 ? <li className="py-2 text-sm text-ink/55">{t("common.empty")}</li> : null}
            {templates.data?.map((tp) => (
              <li key={tp.id} className="py-2 text-sm">
                <p className="font-semibold flex flex-wrap items-center gap-2">{tp.name} <Badge>{t(`inbox.channel.${tp.channel}`)}</Badge> <Badge tone={tp.category === "marketing" ? "amber" : "neutral"}>{t(`inbox.category.${tp.category}`)}</Badge></p>
                <p className="text-ink/70"><span className="text-ink/45">EN</span> {tp.body.en}</p>
                <p className="text-ink/70"><span className="text-ink/45">ES</span> {tp.body.es}</p>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <StaffSheet editing={editing} onClose={() => setEditing(null)} />
      <ConfirmDialog
        open={confirmReset !== null}
        title={confirmReset === "remove" ? t("settings.demoData.remove") : t("settings.demoData.reload")}
        body={t("settings.demoData.sub")}
        confirmLabel={confirmReset === "remove" ? t("settings.demoData.remove") : t("settings.demoData.reload")}
        keepLabel={t("common.cancel")}
        danger={confirmReset === "remove"}
        onKeep={() => setConfirmReset(null)}
        onConfirm={() => { const r = confirmReset === "reload"; setConfirmReset(null); reset.mutate(r); }}
      />
      <p className="text-xs text-ink/40">{locale === "es" ? "Zona horaria" : "Time zone"}: {config.agency.timezone}</p>
    </div>
  );
}

function StaffSheet({ editing, onClose }: { editing: User | "new" | null; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const { toast } = useToast();
  const u = editing && editing !== "new" ? editing : null;
  const key = u?.id ?? "new";
  return (
    <Sheet open={editing !== null} onClose={onClose} title={u ? `${t("settings.staff.edit")}: ${u.name}` : t("settings.staff.add")}>
      {editing !== null ? <StaffForm key={key} user={u} onDone={() => { qc.invalidateQueries({ queryKey: ["settings"] }); qc.invalidateQueries({ queryKey: ["config"] }); toast({ title: t("settings.staff.saved"), tone: "success" }); onClose(); }} /> : null}
    </Sheet>
  );
}

function StaffForm({ user, onDone }: { user: User | null; onDone: () => void }) {
  const t = useT();
  const { toast } = useToast();
  const [f, setF] = useState({
    name: user?.name ?? "", email: user?.email ?? "", role: (user?.role === "executor" ? "agent" : user?.role ?? "agent") as (typeof ROLES)[number], locale: user?.locale ?? "en",
    title: user?.title ?? "", phone: user?.phone ?? "", npn: user?.npn ?? "", active: user?.active ?? true,
    licenses: (user?.licenses ?? []).map((l) => ({ state: l.state, number: l.number, kind: l.kind ?? "" })),
  });
  const set = (k: keyof typeof f, v: unknown) => setF((x) => ({ ...x, [k]: v }));
  const save = useMutation({
    mutationFn: () => callAction<"agency.user.upsert", User>("agency.user.upsert", {
      id: user?.id, name: f.name, email: f.email, role: f.role, locale: f.locale as "en" | "es", title: f.title || undefined, phone: f.phone || undefined, npn: f.npn || undefined, active: f.active,
      licenses: f.licenses.filter((l) => l.number.trim()).map((l) => ({ state: l.state, number: l.number.trim(), kind: l.kind.trim() || undefined })),
    }),
    onSuccess: onDone,
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  return (
    <form className="space-y-3" onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }}>
      <Input label={t("settings.staff.name")} required value={f.name} onChange={(e) => set("name", e.target.value)} />
      <Input label={t("settings.staff.email")} type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} />
      <div className="grid grid-cols-2 gap-3">
        <Select label={t("settings.staff.role")} value={f.role} onChange={(e) => set("role", e.target.value)}>{ROLES.map((r) => <option key={r} value={r}>{t(`role.${r}`)}</option>)}</Select>
        <Select label={t("common.language")} value={f.locale} onChange={(e) => set("locale", e.target.value)}>
          <option value="en">{t("common.english")}</option>
          <option value="es">{t("common.spanish")}</option>
        </Select>
        <Input label={t("settings.staff.title")} value={f.title} onChange={(e) => set("title", e.target.value)} />
        <Input label={t("settings.staff.phone")} type="tel" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
      </div>
      <Input label={t("settings.staff.npn")} value={f.npn} onChange={(e) => set("npn", e.target.value)} />
      <div>
        <span className="mb-1 block text-sm font-semibold text-ink/80">{t("settings.staff.licenses")}</span>
        <ul className="space-y-2">
          {f.licenses.map((l, i) => (
            <li key={i} className="grid grid-cols-[80px_1fr_1fr_auto] gap-2 items-end">
              <Select aria-label={t("setup.identity.state")} value={l.state} onChange={(e) => set("licenses", f.licenses.map((x, j) => (j === i ? { ...x, state: e.target.value } : x)))}>{US_STATES.map((st) => <option key={st} value={st}>{st}</option>)}</Select>
              <Input aria-label={t("setup.identity.licenseNumber")} placeholder={t("setup.identity.licenseNumber")} value={l.number} onChange={(e) => set("licenses", f.licenses.map((x, j) => (j === i ? { ...x, number: e.target.value } : x)))} />
              <Input aria-label={t("setup.identity.licenseKind")} placeholder={t("setup.identity.licenseKind")} value={l.kind} onChange={(e) => set("licenses", f.licenses.map((x, j) => (j === i ? { ...x, kind: e.target.value } : x)))} />
              <Button type="button" variant="ghost" size="sm" aria-label={t("setup.identity.remove")} onClick={() => set("licenses", f.licenses.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>
            </li>
          ))}
        </ul>
        <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => set("licenses", [...f.licenses, { state: "TX", number: "", kind: "" }])}>{t("setup.identity.addLicense")}</Button>
      </div>
      <Checkbox label={t("settings.staff.active")} checked={f.active} onChange={(v) => set("active", v)} />
      <Button type="submit" size="lg" className="w-full" loading={save.isPending}>{t("common.save")}</Button>
    </form>
  );
}

function ActivationCard({ view }: { view?: SettingsView }) {
  const t = useT();
  const { locale } = useLocale();
  const { config } = useConfig();
  const qc = useQueryClient();
  const { toast } = useToast();
  const candidates = config.connectors.filter((c) => c.state !== "live" && !["database", "documents", "auth"].includes(c.id));
  const [items, setItems] = useState<string[]>([]);
  const [notes, setNotes] = useState("");
  const [contact, setContact] = useState({ name: config.user.name, email: config.user.email, phone: "" });
  const send = useMutation({
    mutationFn: () => callAction<"agency.activation.request", ActivationRequest>("agency.activation.request", { items, notes: notes || undefined, contact: { name: contact.name, email: contact.email || undefined, phone: contact.phone || undefined } }),
    onSuccess: () => {
      setItems([]); setNotes("");
      qc.invalidateQueries({ queryKey: ["settings"] });
      toast({ title: t("settings.activation.sent"), tone: "success" });
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  return (
    <Card>
      <CardHeader title={t("settings.activation")} subtitle={t("settings.activation.sub")} />
      <div className="grid gap-5 px-5 pb-5 md:grid-cols-2">
        <form className="space-y-3" onSubmit={(e: FormEvent) => { e.preventDefault(); if (items.length) send.mutate(); }}>
          <p className="text-sm font-semibold text-ink/80">{t("settings.activation.items")}</p>
          <div className="grid gap-1 sm:grid-cols-2">
            {candidates.map((c) => (
              <Checkbox key={c.id} label={`${c.label} (${t(`state.${c.state}`)})`} checked={items.includes(c.id)} onChange={(v) => setItems(v ? [...items, c.id] : items.filter((x) => x !== c.id))} />
            ))}
          </div>
          <Textarea label={t("settings.activation.notes")} value={notes} onChange={(e) => setNotes(e.target.value)} className="min-h-[72px]" />
          <div className="grid gap-2 sm:grid-cols-3">
            <Input label={t("settings.activation.contact")} required value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
            <Input label={t("settings.activation.contactEmail")} type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
            <Input label={t("settings.activation.contactPhone")} type="tel" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
          </div>
          <Button type="submit" disabled={!items.length || !contact.name.trim()} loading={send.isPending}>{t("settings.activation.request")}</Button>
        </form>
        <div>
          <p className="text-sm font-semibold text-ink/80 mb-1">{t("settings.activation.past")}</p>
          {!view?.activationRequests.length ? <p className="text-sm text-ink/55">{t("settings.activation.none")}</p> : null}
          <ul className="divide-y divide-ink/5">
            {view?.activationRequests.map((r) => (
              <li key={r.id} className="py-2 text-sm">
                <p className="font-semibold">{r.items.join(", ")} <span className="text-ink/50 font-normal">· {shortDate(r.createdAt, locale)} · {r.contact.name}</span></p>
                {r.notes ? <p className="text-ink/70">{r.notes}</p> : null}
                <p className="text-xs text-ink/55">{t("settings.activation.remaining")}: {r.remaining.join("; ") || "—"}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Card>
  );
}

type AutomationSettings = { renewalReminders: { enabled: boolean; daysBefore: number[]; autoText: boolean }; noticeEscalation: { enabled: boolean; hoursAfterSla: number }; birthdays: { enabled: boolean }; retention: { enabled: boolean; increasePct: number }; crossSell: { enabled: boolean }; ownerDigest: { enabled: boolean; weekday: number; hour: number; email: boolean }; afterHours: { enabled: boolean; open: string; close: string; days: number[] } };
type AutomationStatus = { automations: AutomationSettings; lastRun: { at: string; ran: string[]; counts: Record<string, number>; notes: string[] } | null; digest: { at: string; markdown: string } | null; timezone: string };

/** The scheduled rules: on/off per rule, the two numbers that matter, Run now, the last run and the latest digest. */
function AutomationsCard() {
  const t = useT();
  const { locale } = useLocale();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [showDigest, setShowDigest] = useState(false);
  const q = useQuery({ queryKey: ["automations"], queryFn: async () => (await callAction<"agency.automations.status", AutomationStatus>("agency.automations.status", {})).result });
  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) => callAction("agency.settings.update", { automations: patch }),
    onSuccess: () => { toast({ title: t("automations.saved"), tone: "success" }); qc.invalidateQueries({ queryKey: ["automations"] }); qc.invalidateQueries({ queryKey: ["settings"] }); },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const run = useMutation({
    mutationFn: () => callAction<"agency.automations.run", { ran: string[]; counts: Record<string, number> }>("agency.automations.run", { force: true }),
    onSuccess: (r) => { toast({ title: `${t("automations.ran")}: ${r.result.ran.join(", ") || "—"}`, body: Object.entries(r.result.counts).map(([k, v]) => `${k} ${v}`).join(" · "), tone: "success" }); for (const k of ["automations", "today", "notices", "pipeline", "documents"]) qc.invalidateQueries({ queryKey: [k] }); },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const a = q.data?.automations;
  if (!a) return null;
  const Row = ({ id, title, sub, enabled, children }: { id: keyof AutomationSettings; title: string; sub: string; enabled: boolean; children?: React.ReactNode }) => (
    <li className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-start">
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{title}</p>
        <p className="text-sm text-ink/60">{sub}</p>
        {children ? <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">{children}</div> : null}
      </div>
      <Checkbox label={t("automations.enabled")} checked={enabled} onChange={(v) => save.mutate({ [id]: { enabled: v } })} />
    </li>
  );
  return (
    <Card>
      <CardHeader title={t("automations.title")} subtitle={t("automations.sub")} action={<Button size="sm" variant="outline" onClick={() => run.mutate()} loading={run.isPending}><RotateCcw className="h-4 w-4" />{t("automations.runNow")}</Button>} />
      <ul className="divide-y divide-ink/5">
        <Row id="renewalReminders" title={t("automations.renewalReminders")} sub={t("automations.renewalReminders.sub")} enabled={a.renewalReminders.enabled}>
          <Checkbox label={t("automations.renewalReminders.autoText")} checked={a.renewalReminders.autoText} onChange={(v) => save.mutate({ renewalReminders: { autoText: v } })} />
        </Row>
        <Row id="noticeEscalation" title={t("automations.noticeEscalation")} sub={t("automations.noticeEscalation.sub")} enabled={a.noticeEscalation.enabled}>
          <label className="flex items-center gap-2"><Input type="number" min={0} max={72} className="w-24" defaultValue={a.noticeEscalation.hoursAfterSla} onBlur={(e) => { const v = Number(e.target.value); if (v !== a.noticeEscalation.hoursAfterSla) save.mutate({ noticeEscalation: { hoursAfterSla: v } }); }} /> {t("automations.hours")}</label>
        </Row>
        <Row id="birthdays" title={t("automations.birthdays")} sub={t("automations.birthdays.sub")} enabled={a.birthdays.enabled} />
        <Row id="retention" title={t("automations.retention")} sub={t("automations.retention.sub")} enabled={a.retention.enabled}>
          <label className="flex items-center gap-2">{t("automations.retention.pct")} <Input type="number" min={1} max={100} className="w-24" defaultValue={a.retention.increasePct} onBlur={(e) => { const v = Number(e.target.value); if (v !== a.retention.increasePct) save.mutate({ retention: { increasePct: v } }); }} /></label>
        </Row>
        <Row id="crossSell" title={t("automations.crossSell")} sub={t("automations.crossSell.sub")} enabled={a.crossSell.enabled} />
        <Row id="ownerDigest" title={t("automations.ownerDigest")} sub={t("automations.ownerDigest.sub")} enabled={a.ownerDigest.enabled}>
          <Checkbox label={t("automations.ownerDigest.email")} checked={a.ownerDigest.email} onChange={(v) => save.mutate({ ownerDigest: { email: v } })} />
        </Row>
        <Row id="afterHours" title={t("extras.afterHours")} sub={t("extras.afterHours.sub")} enabled={a.afterHours.enabled}>
          <label className="flex items-center gap-2">{t("extras.afterHours.open")} <Input type="time" className="w-32" defaultValue={a.afterHours.open} onBlur={(e) => { if (e.target.value && e.target.value !== a.afterHours.open) save.mutate({ afterHours: { open: e.target.value } }); }} /></label>
          <label className="flex items-center gap-2">{t("extras.afterHours.close")} <Input type="time" className="w-32" defaultValue={a.afterHours.close} onBlur={(e) => { if (e.target.value && e.target.value !== a.afterHours.close) save.mutate({ afterHours: { close: e.target.value } }); }} /></label>
        </Row>
      </ul>
      <div className="border-t border-ink/5 px-5 py-3 text-sm">
        <p className="text-ink/60">
          {t("automations.lastRun")}: {q.data?.lastRun ? `${shortDate(q.data.lastRun.at, locale)} · ${q.data.lastRun.ran.join(", ") || "—"} · ${Object.entries(q.data.lastRun.counts).map(([k, v]) => `${k} ${v}`).join(", ")}` : t("automations.never")}
        </p>
        {q.data?.lastRun?.notes.length ? <ul className="mt-1 list-disc pl-5 text-xs text-ink/55">{q.data.lastRun.notes.map((n, i) => <li key={i}>{n}</li>)}</ul> : null}
        <div className="mt-2">
          <button type="button" onClick={() => setShowDigest((v) => !v)} className="font-semibold text-primary-dark hover:underline">{t("automations.digest")}{q.data?.digest ? ` · ${shortDate(q.data.digest.at, locale)}` : ""}</button>
          {showDigest ? (q.data?.digest ? <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap rounded-xl bg-ink/[0.03] p-3 text-xs">{q.data.digest.markdown}</pre> : <p className="mt-1 text-ink/55">{t("automations.noDigest")}</p>) : null}
        </div>
      </div>
    </Card>
  );
}
